/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { camelCase, upperFirst } from "lodash";
import { Navigate, useParams } from "react-router-dom";

import { Alert } from "@rewards-web/shared/components/alert";
import { Button } from "@rewards-web/shared/components/button";
import { PageLoadingState } from "@rewards-web/shared/components/page-loading-state";
import { Typography } from "@rewards-web/shared/components/typography";
import { SurveyQuestionType } from "@rewards-web/shared/graphql-types";
import { reportError } from "@rewards-web/shared/modules/error";
import { AppTheme } from "@rewards-web/shared/style/types";

import { QuestionCategory } from "../../components/question-category";
import { QuestionText } from "../../components/question-text";
import { ResponseBreakdownCard } from "../../components/response-breakdown-card";
import { isSurveyTypeSupportedByUI } from "../../constants";
import { EmployeeFeedbackTable } from "./employee-feedback-table";
import { useSurveyQuestionDetailsPageDataQuery } from "./question-details-page-data.generated";

export function SurveyQuestionDetailsPageContents() {
  const params = useParams();

  const questionType =
    SurveyQuestionType[
      // converts "question_type" to "QuestionType"
      upperFirst(
        camelCase(params.question_type!)
      ) as keyof typeof SurveyQuestionType
    ];

  const validSurveyType = isSurveyTypeSupportedByUI(questionType);

  const { data, error } = useSurveyQuestionDetailsPageDataQuery({
    variables: {
      questionType,
    },
    onError: reportError,
    skip: !validSurveyType,
  });

  if (!validSurveyType) {
    return <Navigate to="/surveys/pulse" replace />;
  }

  if (error) {
    return (
      <Alert
        severity="error"
        message="Something went wrong. Please try again later."
      />
    );
  }

  if (!data) {
    return <PageLoadingState />;
  }

  return (
    <>
      <Button
        variant="text"
        startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
        label="Back"
        width="auto"
        typographyVariant="body"
        linkTo="/surveys/pulse"
      />
      <div
        css={(theme: AppTheme) => css`
          margin: ${theme.spacing(3)} 0;
        `}
      >
        <QuestionCategory questionType={questionType} variant="subtitle" />
        <Typography
          variant="h4"
          fontWeight={700}
          css={(theme: AppTheme) =>
            css`
              margin-bottom: ${theme.spacing(1)};
            `
          }
        >
          Question
        </Typography>
        <QuestionText questionType={questionType} variant="h2" />
      </div>
      <ResponseBreakdownCard
        questionType={questionType}
        scores={data.getScoreDistributionForSurveyQuestion}
      />
      {questionType !== SurveyQuestionType.Nps && (
        // NPS does not have associated feedback (comments)
        <EmployeeFeedbackTable questionType={questionType} />
      )}
    </>
  );
}
